import React from "react";
import { Link } from "react-router-dom";
import { classnames, Icon, Panel, PlainButton } from "@culturehq/components";

import { useInterests, useLocations, useProfile, useQueryString } from "app/context";
import { useDoor } from "app/hooks";
import { canCreateEvent } from "app/permissions";
import { getFiltersOptions, t, Trans } from "app/utils";

import FilterGroup from "components/common/FilterGroup";
import InterestFilter, { InterestFilterState } from "components/filters/InterestFilter";
import LocationFilter, { LocationFilterState } from "components/filters/LocationFilter";

import DateFilter, { DateFilterState } from "./filters/DateFilter";
import EventTypeFilter, { EventTypeFilterState } from "./filters/EventTypeFilter";
import SponsoredFilter, { SponsoredFilterState } from "./filters/SponsoredFilter";
import UserRelationFilter, { UserRelationFilterState } from "./filters/UserRelationFilter";

import EventSlides from "./EventSlides";

const EventFilters = ({ options, loading, pagination: { totalCount } }) => {
  const { open: openOnMobile, onToggle } = useDoor(false);
  const { profile } = useProfile();
  const { onClearQueryString } = useQueryString();

  const isAFilterApplied = Object.entries(options)
    .filter(option => option[0] !== "allLocations" && option[0] !== "sort")
    .map(option => option[1])
    .filter(array => array).length > 0;
  const { interests } = useInterests();
  const { locations } = useLocations();

  const filters = {
    locations: getFiltersOptions("locationIds", locations, options),
    interests: getFiltersOptions("interestIds", interests, options)
  };

  const eventsWord = totalCount === 1 ? " event" : " events";
  const resultsText = totalCount ? `${t("Showing")} ${totalCount} ${eventsWord}` : t(`No ${eventsWord}`);

  const getMyEventsFilter = () => {
    switch (options.userRelation) {
      case "invited":
        return ["were invited"];
      case "host":
        return ["hosted"];
      case "responded":
        return ["responded to"];
      default:
        return [];
    }
  };

  const getSponsoredFilter = () => {
    if (options.sponsored) {
      return ["sponsored"];
    }

    return ["not sponsored"];
  };

  const getWhenEventsFilter = () => {
    switch (options.when) {
      case "past":
        return ["Past"];
      case "present":
        return ["Ongoing"];
      case "future":
        return ["Upcoming"];
      default:
        return [];
    }
  };

  return (
    <>
      <div className="Filters SidePanel col-sm-12 col-sm-8 col-md-9 col-lg-2">
        <div className="row">
          {canCreateEvent(profile) && (
            <div className="Filters__button clearfix col-xs-6 col-lg-12">
              <Link to="/events/new/type" className="button create">
                <Icon icon="calendar-add" />
                <span><Trans>Create Event</Trans></span>
              </Link>
            </div>
          )}
          <div
            className={
              classnames(
                "Filters__button",
                "toggle-filters",
                "clearfix",
                canCreateEvent(profile) ? "col-xs-6" : "col-xs-12",
                "col-lg-12"
              )
            }
          >
            <PlainButton onClick={onToggle} className="button toggle-filters">
              <Icon icon="calendar-check" />
              {openOnMobile ? t("Hide Filters") : t("Find Events")}
            </PlainButton>
          </div>
        </div>
        <Panel className={openOnMobile ? "open" : "closed"}>
          <div className="chq-pan--hd">
            <EventSlides options={options} />
            <h1>
              <Icon icon="calendar-check" />
              {" "}
              <Trans><Trans>Find Events</Trans></Trans>
            </h1>
            <hr />
          </div>
          <Panel.Body>
            <DateFilter />
            <LocationFilter />
            <UserRelationFilter />
            <EventTypeFilter />
            <SponsoredFilter />
            <InterestFilter />
          </Panel.Body>
          <Panel.Footer className="Filters__Footer">
            {isAFilterApplied && !loading && (
              <>
                <div className="Filters__Footer--header">
                  <h2 className="Filters__Applied"><Trans>Applied filters</Trans></h2>
                  <PlainButton className="Filters__Footer--clear" onClick={onClearQueryString}>
                    <Trans>Clear</Trans>
                  </PlainButton>
                </div>
                <hr />
                <div className="Filters__Active">
                  <DateFilterState />
                  <UserRelationFilterState />
                  <SponsoredFilterState />
                  <EventTypeFilterState />
                  <LocationFilterState />
                  <InterestFilterState />
                </div>
                <div className="Filters__Results">
                  <p className="results" role="status">
                    <p>{resultsText}{" "}<Trans>that match your filters.</Trans></p>
                    <p>
                      <Trans>You have selected event filters that include the following:</Trans>
                    </p>
                    {(options.when || options.userRelation || options.sponsored !== null) && (
                      <p>
                        <FilterGroup key="when" group={getWhenEventsFilter()} />
                        {" "}
                        <Trans>{options.when ? "e" : "E"}vents</Trans>
                        <FilterGroup key="my-events" group={getMyEventsFilter()} prefix="that you" />
                        {options.sponsored !== null && <FilterGroup key="sponsored" group={getSponsoredFilter()} prefix={t("that are")} />}
                      </p>
                    )}
                    <p>
                      <FilterGroup key="types" group={options.eventTypes?.map(opt => decodeURI(opt))} prefix={t("Event types include")} orRelation />
                    </p>
                    <p>
                      <FilterGroup key="locations" group={filters.locations} prefix="Locations include" orRelation />
                    </p>
                    <p>
                      <FilterGroup key="interests" group={filters.interests} prefix="Tagged interests include" orRelation />
                    </p>
                  </p>
                </div>
              </>
            )}
          </Panel.Footer>
        </Panel>
      </div>
    </>
  );
};

export default EventFilters;
